'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Script from 'next/script';

const GTM_FALLBACK_MS = 5000;

interface DeferredGoogleTagManagerProps {
  gtmId: string;
}

export default function DeferredGoogleTagManager({
  gtmId,
}: DeferredGoogleTagManagerProps) {
  const pathname = usePathname();
  // Keep marketing tags out of the initial job-results render. Funnel events
  // continue to queue in dataLayer until interaction or the fallback loads GTM.
  const shouldDeferForInitialRender =
    pathname === '/tao-cv' ||
    pathname === '/viec-lam' ||
    pathname.startsWith('/viec-lam/') ||
    pathname === '/cong-ty' ||
    pathname.startsWith('/viec-lam/');
  const [shouldLoadContainer, setShouldLoadContainer] = useState(
    !shouldDeferForInitialRender
  );
  const scriptUrl = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;

  useEffect(() => {
    if (!shouldDeferForInitialRender) {
      if (shouldLoadContainer) return;

      const timeoutId = window.setTimeout(
        () => setShouldLoadContainer(true),
        0
      );

      return () => window.clearTimeout(timeoutId);
    }

    if (shouldLoadContainer) return;

    const controller = new AbortController();
    let loadRequested = false;
    let timeoutId = 0;

    const loadContainer = () => {
      if (loadRequested) return;

      loadRequested = true;
      window.clearTimeout(timeoutId);
      controller.abort();
      setShouldLoadContainer(true);
    };

    const loadWhenPageIsHidden = () => {
      if (document.visibilityState === 'hidden') loadContainer();
    };

    const loadOnPointerIntent = (event: PointerEvent) => {
      const target = event.target;

      if (
        target instanceof Element &&
        target.closest(
          'a, button, input, select, textarea, [role="button"], [role="link"]'
        )
      ) {
        loadContainer();
      }
    };

    timeoutId = window.setTimeout(loadContainer, GTM_FALLBACK_MS);

    window.addEventListener('pointerover', loadOnPointerIntent, {
      passive: true,
      signal: controller.signal,
    });
    window.addEventListener('pointerdown', loadContainer, {
      once: true,
      passive: true,
      signal: controller.signal,
    });
    window.addEventListener('touchstart', loadContainer, {
      once: true,
      passive: true,
      signal: controller.signal,
    });
    window.addEventListener('scroll', loadContainer, {
      once: true,
      passive: true,
      signal: controller.signal,
    });
    window.addEventListener('keydown', loadContainer, {
      once: true,
      signal: controller.signal,
    });
    window.addEventListener('focusin', loadContainer, {
      once: true,
      signal: controller.signal,
    });
    document.addEventListener('visibilitychange', loadWhenPageIsHidden, {
      signal: controller.signal,
    });

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [shouldDeferForInitialRender, shouldLoadContainer]);

  return (
    <>
      <Script id="_next-gtm-init" strategy="afterInteractive">
        {`window.dataLayer=window.dataLayer||[];window.dataLayer.push({'gtm.start':Date.now(),event:'gtm.js'});`}
      </Script>
      {shouldLoadContainer ? (
        <Script
          id="_next-gtm"
          data-ntpc="GTM"
          src={scriptUrl}
          strategy={
            shouldDeferForInitialRender ? 'afterInteractive' : 'lazyOnload'
          }
        />
      ) : null}
    </>
  );
}
